import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Bell, BellOff, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const VAPID_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function PushNotificationToggle() {
  const [permission, setPermission] = useState('default');
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!('Notification' in window) || !('serviceWorker' in navigator)) return;
    setPermission(Notification.permission);

    // Check existing subscription
    navigator.serviceWorker.ready.then(reg => {
      reg.pushManager.getSubscription().then(sub => {
        setSubscribed(!!sub);
      });
    });
  }, []);

  const handleSubscribe = async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      toast.error('Tu navegador no soporta notificaciones push');
      return;
    }

    setLoading(true);
    try {
      // Register service worker
      const registration = await navigator.serviceWorker.register('/sw-push.js');
      await navigator.serviceWorker.ready;

      // Request permission
      const perm = await Notification.requestPermission();
      setPermission(perm);

      if (perm !== 'granted') {
        toast.error('Necesitas permitir las notificaciones para activarlas');
        setLoading(false);
        return;
      }

      // Subscribe
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_KEY)
      });

      // Send to backend
      await axios.post(`${API_URL}/api/push/subscribe`, {
        subscription: subscription.toJSON()
      }, { withCredentials: true });

      setSubscribed(true);
      toast.success('Notificaciones activadas');
    } catch (err) {
      console.error('Push subscription error:', err);
      toast.error('Error al activar notificaciones');
    } finally {
      setLoading(false);
    }
  };

  const handleUnsubscribe = async () => {
    setLoading(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        await subscription.unsubscribe();
      }
      await axios.post(`${API_URL}/api/push/unsubscribe`, {}, { withCredentials: true });
      setSubscribed(false);
      toast.success('Notificaciones desactivadas');
    } catch (err) {
      toast.error('Error al desactivar notificaciones');
    } finally {
      setLoading(false);
    }
  };

  // Don't render if push not supported
  if (!('Notification' in window) || !('serviceWorker' in navigator) || !VAPID_KEY) {
    return null;
  }

  if (permission === 'denied') {
    return (
      <div className="flex items-center gap-2 px-2 py-1">
        <BellOff className="w-4 h-4 text-gray-500" />
        <span className="text-gray-500 text-xs">Notificaciones bloqueadas en tu navegador</span>
      </div>
    );
  }

  return (
    <Button
      variant="ghost"
      onClick={subscribed ? handleUnsubscribe : handleSubscribe}
      disabled={loading}
      className={`text-xs gap-2 ${subscribed ? 'text-emerald-400 hover:text-red-400 hover:bg-red-500/10' : 'text-gray-400 hover:text-emerald-400 hover:bg-emerald-500/10'}`}
      data-testid="push-notification-toggle"
    >
      {loading ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : subscribed ? (
        <>
          <Bell className="w-4 h-4" />
          Notificaciones activas
        </>
      ) : (
        <>
          <BellOff className="w-4 h-4" />
          Activar notificaciones
        </>
      )}
    </Button>
  );
}
